import type {
  MetricTierConfiguration,
  ProcessedTierConfiguration,
  EstimateConfiguration,
  PeriodMetric,
} from '../types';

type TierConfigResult = {
  result: ProcessedTierConfiguration | null;
  complete?: boolean;
};

export const getTierConfig = (
  index: number,
  min: number,
  max: number,
  config: MetricTierConfiguration,
  lastUpperBound: number
): TierConfigResult => {
  const { tier, commission_percent, adjusted_upper_bound } = config;

  const result: TierConfigResult = {
    result: null,
  };

  // Remove spans that are above or below the upper and lower bounds
  if (
    adjusted_upper_bound <= min ||
    (lastUpperBound >= max && adjusted_upper_bound > max)
  ) {
    return result;
  }

  const name = `tier${tier}`;
  const configResult: Partial<ProcessedTierConfiguration> = {
    tier,
    name,
    adjusted_upper_bound,
    adjusted_lower_bound: lastUpperBound,
    commission_percent,
  };

  if (adjusted_upper_bound > max) {
    configResult.adjusted_upper_bound = max;
    result.complete = true;
  }

  result.result = configResult as ProcessedTierConfiguration;

  return result;
};

/**
 * Calculate the correct commission estimates configuration based on sometime
 * faulty data from the API.
 * @param data
 */
export default function processCommissionEstimates(
  data: any
): EstimateConfiguration {
  const estimateResult: Partial<EstimateConfiguration> = {
    product_type_id: data.product_type_id,
    category: data.category,
    client_commission_percent: data.client_commission_percent,
  };

  const periods: PeriodMetric[] = ['monthly', 'weekly', 'daily', 'hourly'];

  periods.forEach((period) => {
    const periodData = data[period];
    if (
      periodData.adjusted_min_rate === null ||
      periodData.adjusted_max_rate === null ||
      !periodData
    ) {
      estimateResult[period] = null;
    } else {
      const min: number = periodData.adjusted_min_rate;
      const max: number = periodData.adjusted_max_rate;

      let lastAdjustedUpperBound = min;
      const tiers: ProcessedTierConfiguration[] = [];

      // if the period has valid min and max rates but no tier information add a single tier
      if (periodData.tiers.length === 0) {
        const singleTier: ProcessedTierConfiguration = {
          tier: 0,
          name: `tier0`,
          adjusted_upper_bound: max,
          adjusted_lower_bound: min,
          commission_percent:
            estimateResult.client_commission_percent as number,
        };
        tiers.push(singleTier);
      } else {
        let index = 0;
        for (const tierData of periodData.tiers) {
          const { adjusted_upper_bound, tier, commission_percent } = tierData;
          const { result, complete } = getTierConfig(
            index,
            min,
            max,
            tierData,
            lastAdjustedUpperBound
          );

          if (result) {
            tiers.push(result);
            lastAdjustedUpperBound = result.adjusted_upper_bound;
          }

          index += 1;

          if (complete) {
            break;
          }
        }

        // If the last tier doesn't fill the span add a final tier with the client's default rate.
        if (
          tiers.length > 0 &&
          tiers[tiers.length - 1].adjusted_upper_bound < max
        ) {
          const finalTier: ProcessedTierConfiguration = {
            tier: tiers.length,
            name: `tier${tiers.length}`,
            adjusted_upper_bound: max,
            adjusted_lower_bound: tiers[tiers.length - 1].adjusted_upper_bound,
            commission_percent:
              estimateResult.client_commission_percent as number,
          };
          tiers.push(finalTier);
        }
      }

      estimateResult[period] = {
        adjusted_min_rate: periodData.adjusted_min_rate,
        adjusted_max_rate: periodData.adjusted_max_rate,
        tiers,
      };
    }
  });

  return estimateResult as EstimateConfiguration;
}
